import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError, authHeaders } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, FileDown, User, IdCard, ShieldCheck, FileSignature, Bike, AlertCircle, Save } from "lucide-react";
import { toast } from "sonner";
import DocumentUpload from "@/components/DocumentUpload";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const DOC_FIELDS = [
  { key: "id_card_front_url", label: "Documento d'identità (fronte)", desc: "Carta d'identità o passaporto" },
  { key: "id_card_back_url", label: "Documento d'identità (retro)" },
  { key: "license_front_url", label: "Patente (fronte)" },
  { key: "license_back_url", label: "Patente (retro)" },
];

export default function AdminCustomerDossier() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [licenseForm, setLicenseForm] = useState({ license_number: "", license_issue_date: "", license_expiry_date: "" });
  const [savingLicense, setSavingLicense] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    api.get(`/admin/customers/${customerId}`).then(({ data }) => {
      setData(data);
      setLicenseForm({
        license_number: data.user.license_number || "",
        license_issue_date: data.user.license_issue_date || "",
        license_expiry_date: data.user.license_expiry_date || "",
      });
    });
  }, [customerId, reload]);

  const updateDocument = async (key, url) => {
    try {
      await api.patch(`/admin/customers/${customerId}/documents`, { [key]: url });
      toast.success(url ? "Documento caricato" : "Documento rimosso");
      setReload((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const saveLicense = async () => {
    setSavingLicense(true);
    try {
      await api.patch(`/admin/customers/${customerId}/documents`, licenseForm);
      toast.success("Dati patente aggiornati");
      setReload((k) => k + 1);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSavingLicense(false);
    }
  };

  const downloadDossier = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/customers/${customerId}/dossier-pdf`, {
        headers: authHeaders(),
      });
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `fascicolo_${data.user.surname || ""}_${data.user.name || ""}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (!data) return <div className="text-zinc-400">Caricamento…</div>;

  const u = data.user;
  const missing = data.missing_documents || [];
  const licExpiry = u.license_expiry_date ? new Date(u.license_expiry_date) : null;
  const licDays = licExpiry ? Math.ceil((licExpiry - new Date()) / 86400000) : null;

  return (
    <div className="space-y-6" data-testid="customer-dossier-page">
      <Button variant="ghost" onClick={() => navigate("/admin/customers")} className="text-zinc-400 hover:text-zinc-100">
        <ArrowLeft className="w-4 h-4 mr-1" /> Torna ai clienti
      </Button>

      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-[#FFD700]">Fascicolo digitale</p>
          <h1 className="font-display text-4xl font-bold mt-1">{u.name} {u.surname}</h1>
          <p className="text-zinc-400 text-sm mt-1">{u.email} · {u.phone || "—"}</p>
        </div>
        <Button onClick={downloadDossier} className="bg-[#FFD700] hover:bg-[#FACC15] text-black font-semibold" data-testid="dossier-pdf-download">
          <FileDown className="w-4 h-4 mr-1" /> Scarica PDF
        </Button>
      </div>

      {/* Anagrafica */}
      <Section icon={User} title="Anagrafica">
        <div className="grid sm:grid-cols-3 gap-3 text-sm">
          <Info label="Data nascita" value={u.birth_date} />
          <Info label="Luogo nascita" value={u.birth_place} />
          <Info label="N. Documento" value={u.document_number} mono />
          <Info label="Via" value={u.address_street} colSpan={2} />
          <Info label="Città / CAP" value={u.address_city ? `${u.address_city} - ${u.address_zip}` : null} />
        </div>
      </Section>

      {/* Documenti */}
      <Section icon={IdCard} title="Documenti" badge={missing.length > 0 && (
        <span className="inline-flex items-center gap-1 text-xs text-amber-400 bg-amber-500/15 border border-amber-500/30 px-2 py-0.5 rounded-full">
          <AlertCircle className="w-3 h-3" /> {missing.length} mancanti
        </span>
      )}>
        <div className="grid sm:grid-cols-2 gap-4">
          {DOC_FIELDS.map((d) => (
            <DocumentUpload
              key={d.key}
              value={u[d.key]}
              onChange={(url) => updateDocument(d.key, url)}
              label={d.label}
              description={d.desc}
              testIdBase={`doc-${d.key.replace(/_/g, "-")}`}
            />
          ))}
        </div>
      </Section>

      {/* Patente */}
      <Section icon={ShieldCheck} title="Dati Patente">
        <div className="grid sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs text-zinc-400">N. Patente</Label>
            <Input value={licenseForm.license_number}
                   onChange={(e) => setLicenseForm({ ...licenseForm, license_number: e.target.value.toUpperCase() })}
                   className="bg-zinc-950 border-zinc-800 mt-1" data-testid="license-number" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Data rilascio</Label>
            <Input type="date" value={licenseForm.license_issue_date}
                   onChange={(e) => setLicenseForm({ ...licenseForm, license_issue_date: e.target.value })}
                   className="bg-zinc-950 border-zinc-800 mt-1" data-testid="license-issue" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Data scadenza</Label>
            <Input type="date" value={licenseForm.license_expiry_date}
                   onChange={(e) => setLicenseForm({ ...licenseForm, license_expiry_date: e.target.value })}
                   className="bg-zinc-950 border-zinc-800 mt-1" data-testid="license-expiry" />
            {licDays != null && (
              <p className={`text-xs mt-1 ${licDays < 0 ? "text-red-400" : licDays <= 60 ? "text-amber-400" : "text-emerald-400"}`}>
                {licDays < 0 ? `Scaduta ${Math.abs(licDays)} giorni fa` : `Tra ${licDays} giorni`}
              </p>
            )}
          </div>
        </div>
        <Button onClick={saveLicense} disabled={savingLicense} className="mt-3 bg-zinc-800 hover:bg-zinc-700 text-zinc-100" data-testid="license-save">
          <Save className="w-4 h-4 mr-1" /> {savingLicense ? "Salvataggio…" : "Salva patente"}
        </Button>
      </Section>

      {/* Consensi */}
      <Section icon={ShieldCheck} title="Consensi & Audit GDPR">
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          <Info label="Contratto accettato" value={u.contract_accepted ? "Sì" : "No"} valueClass={u.contract_accepted ? "text-emerald-400" : "text-red-400"} />
          <Info label="Data accettazione" value={(u.contract_accepted_at || "").slice(0, 19).replace("T", " ") || "—"} />
          <Info label="Registrato il" value={(u.created_at || "").slice(0, 19).replace("T", " ") || "—"} />
        </div>
      </Section>

      {/* Contratti manuali */}
      <Section icon={FileSignature} title={`Contratti manuali (${data.manual_contracts.length})`}>
        {data.manual_contracts.length === 0 ? (
          <p className="text-zinc-500 text-sm">Nessun contratto manuale</p>
        ) : (
          <div className="space-y-2">
            {data.manual_contracts.map((c) => (
              <Link key={c.id} to={`/admin/contracts`} className="block rounded-lg border border-zinc-800 bg-zinc-950 hover:border-zinc-700 px-4 py-3 transition" data-testid={`dossier-contract-${c.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="font-mono text-xs text-[#FFD700]">{c.contract_number || c.id.slice(0,8)}</p>
                    <p className="text-zinc-100">{c.vehicle.model} · {c.vehicle.plate}</p>
                    <p className="text-zinc-500 text-xs">{c.start_at.slice(0,10)} → {c.end_at.slice(0,10)} · {c.days}g</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-lg font-bold text-[#FFD700]">€{c.total_amount.toFixed(2)}</p>
                    <p className={`text-xs ${c.payment_status === "pagato" ? "text-emerald-400" : "text-red-400"}`}>{c.payment_status}</p>
                  </div>
                </div>
                <div className="mt-2 flex gap-2 text-[10px] uppercase tracking-wider">
                  {c.delivery_report && <span className="text-emerald-400">✓ Consegna</span>}
                  {c.return_report && <span className="text-emerald-400">✓ Riconsegna</span>}
                  {c.rental_status === "chiuso" && <span className="text-zinc-500">CHIUSO</span>}
                </div>
              </Link>
            ))}
          </div>
        )}
      </Section>

      {/* Prenotazioni online */}
      <Section icon={Bike} title={`Prenotazioni online (${data.online_bookings.length})`}>
        {data.online_bookings.length === 0 ? (
          <p className="text-zinc-500 text-sm">Nessuna prenotazione</p>
        ) : (
          <div className="space-y-2">
            {data.online_bookings.map((b) => (
              <div key={b.id} className="rounded-lg border border-zinc-800 bg-zinc-950 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-zinc-100">{b.scooter_model} · <span className="font-mono text-xs">{b.scooter_plate}</span></p>
                    <p className="text-zinc-500 text-xs">{b.start_at.slice(0,10)} → {b.end_at.slice(0,10)} · {b.days || "?"}g</p>
                  </div>
                  <div className="text-right">
                    <p className="font-display text-lg font-bold text-[#FFD700]">€{(b.total_amount || 0).toFixed(2)}</p>
                    <p className={`text-xs ${b.payment_status === "pagato" ? "text-emerald-400" : "text-amber-400"}`}>{b.payment_status}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, badge, children }) {
  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-5">
      <div className="flex items-center gap-2 mb-4">
        <Icon className="w-4 h-4 text-[#FFD700]" />
        <h2 className="font-semibold text-zinc-100">{title}</h2>
        {badge && <span className="ml-auto">{badge}</span>}
      </div>
      {children}
    </div>
  );
}

function Info({ label, value, mono, valueClass = "", colSpan }) {
  return (
    <div className={colSpan === 2 ? "sm:col-span-2" : ""}>
      <p className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</p>
      <p className={`text-zinc-100 ${mono ? "font-mono" : ""} ${valueClass}`}>{value || "—"}</p>
    </div>
  );
}
